import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Constants } from '@pwc/pwc-deals-web-common';
import { Checkbox, Icon, Tooltip } from '@pwc/pwc-deals-ui';
import EnableForProjectState from '../security/EnableForProjectState';
import ShowForRole from '../security/ShowForRole';
import { sortKeys } from './contentListSortKeys';
import ListHeaderCell from './ListHeaderCell';
import './ContentListHeader.scss';

const ContentListHeader = ({
  projectId,
  currentSortKey,
  currentSortDir,
  setCurrentSortKey,
  setCurrentSortDir,
  isDragAllowed,
  isDragEnabled,
  areSomeSelected,
  areAllSelected,
  onSelectAll,
}) => {
  const { t } = useTranslation();
  const handleSelectAllOnChange = () => {
    onSelectAll(!areAllSelected);
  };

  const tip = t(
    isDragAllowed
      ? 'kfa.presentationOrderDragWarning'
      : 'kfa.presentationOrderWarning'
  );

  const sortOrderCellClasses = classNames('sort-order-cell', {
    'tooltip-short': !isDragAllowed,
  });

  return (
    <div data-testid="content-list-header" className="content-list-header">
      <EnableForProjectState projectId={projectId}>
        {({ isProjectDisabled }) => (
          <ShowForRole
            roles={Constants.USER_ROLE_GROUPS.INTERNAL}
            projectId={projectId}
          >
            {isDragEnabled && (
              <div className="sort-order-checkbox-cell">
                <Checkbox
                  dataTestId="select-all-for-drag-checkbox"
                  id="select-all-for-drag-checkbox"
                  disabled={!isDragAllowed}
                  onChange={handleSelectAllOnChange}
                  checked={areAllSelected}
                  indeterminate={!areAllSelected && areSomeSelected}
                />
              </div>
            )}
            <div className={sortOrderCellClasses}>
              <ListHeaderCell
                text={t('order')}
                testId="order-header-cell"
                sortKey={sortKeys.sortOrder}
                currentSortKey={currentSortKey}
                sortDir={currentSortDir}
                setCurrentSortKey={setCurrentSortKey}
                setCurrentSortDir={setCurrentSortDir}
                disabled={isProjectDisabled}
              >
                <Tooltip direction="right" tip={tip} dataTestId="order-tooltip">
                  <Icon
                    name="information"
                    className="fill icon-yellow"
                    data-testid="information-icon"
                    width="16"
                  />
                </Tooltip>
              </ListHeaderCell>
            </div>

            <ListHeaderCell
              text={t('lastUpdated')}
              testId="last-updated-header-cell"
              sortKey={sortKeys.dateModified}
              sortDir={currentSortDir}
              currentSortKey={currentSortKey}
              setCurrentSortKey={setCurrentSortKey}
              setCurrentSortDir={setCurrentSortDir}
              disabled={isProjectDisabled}
            />
            <ListHeaderCell
              text={t('status')}
              testId="shared-header-cell"
              sortKey={sortKeys.shared}
              sortDir={currentSortDir}
              currentSortKey={currentSortKey}
              setCurrentSortKey={setCurrentSortKey}
              setCurrentSortDir={setCurrentSortDir}
              disabled={isProjectDisabled}
            />
            <div />
          </ShowForRole>
        )}
      </EnableForProjectState>
    </div>
  );
};

ContentListHeader.propTypes = {
  projectId: PropTypes.string.isRequired,
  currentSortKey: PropTypes.string.isRequired,
  setCurrentSortKey: PropTypes.func.isRequired,
  setCurrentSortDir: PropTypes.func.isRequired,
  isDragAllowed: PropTypes.bool.isRequired,
  isDragEnabled: PropTypes.bool.isRequired,
  areSomeSelected: PropTypes.bool.isRequired,
  areAllSelected: PropTypes.bool.isRequired,
  onSelectAll: PropTypes.func.isRequired,
};

export default ContentListHeader;
